// Stats en direct (D-046), le « Stat'man » de Playbook : au bord du terrain, on choisit
// la joueuse puis l'action ; l'adversaire en un geste ; annuler la dernière action ;
// quart-temps ; « Terminer » écrit le score final. La feuille de stats et le score
// se calculent à partir du journal. Le score passe en direct sur la page publique.
// D-055 : saisie « score seul » (sans joueuse) au choix, et numéros réglables pour ce match.
// D-057 : le même écran, par un lien sans compte, le temps du match.
// D-175, D-176 : un 3e mode, « Table de marque » (src/ui/table.ts), sur le même journal et le même lien sans compte.
// D-103 : sur un match joué, la saisie « après coup » ne passe jamais le match en direct et ne touche
// pas au score ; le score ne se recalcule d'après la saisie que sur demande, toutes les actions marquées
// saisies. « Qui ? » : les retenues de la composition, celles placées depuis l'autre équipe comprises.
import {
  activeStatLink, addAction, canTrack, deleteAction, eventById, statLinkCreate, statLinkRevoke, matchAccounts, matchActions, matchLineup, matchLive, matchScoreFromActions, matchSetNumbers, matchesFor, occurrenceRoster,
  responsesFor, statLinkAct, statLinkInfo, statLinkLive, statLinkNumbers, statLinkUndo, teamRoster, type MatchRow,
} from '../lib/api';
import { resolveAttendance } from '../domain/attendance';
import { attCtx, leadsFor, loadAvail } from './availability-data';
import { ACTION_LABEL, boxScore, byQuarter, score, type Action, type ActionKind } from '../domain/boxscore';
import { localDate } from '../domain/schedule';
import { displayName } from '../domain/labels';
import { retainedOf } from '../domain/match-day';
import { scoreCheck, statsMode } from '../domain/match-phase';
import { squadName, teamOf, type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast } from './dom';
import { dataHref, legalFootLink } from './legal-links';
import { offlineResume, openTable } from './table';
import { renderClubSheetApp, renderTableAdmin } from './table-sheet';
import type { TeamGender } from '../domain/labels';

const QLABEL = (q: number) => (q <= 4 ? `Q${q}` : `P${q - 4}`);
const PAD: [ActionKind, string][] = [
  ['p2', '+2'], ['p3', '+3'], ['ft', 'LF'], ['p2_miss', '2 raté'], ['p3_miss', '3 raté'], ['ft_miss', 'LF raté'],
  ['oreb', 'Rebond off.'], ['dreb', 'Rebond déf.'], ['ast', 'Passe déc.'], ['stl', 'Intercep.'], ['blk', 'Contre'], ['tov', 'Balle perdue'], ['foul', 'Faute'],
];
const OPP: [ActionKind, string][] = [['opp1', '+1'], ['opp2', '+2'], ['opp3', '+3'], ['opp_foul', 'Faute']];
const US: [ActionKind, string][] = [['us1', '+1'], ['us2', '+2'], ['us3', '+3']];
type Mode = 'full' | 'score' | 'table';
const modeKey = (eventId: string) => `arceau:ls-mode:${eventId}`;
/** Le mode gardé sur ce téléphone ; sinon la table de marque en amical (D-175 : proposée d'office), les stats ailleurs. */
const readMode = (eventId: string, amical = false): Mode => {
  try { const v = localStorage.getItem(modeKey(eventId)); return v === 'score' || v === 'table' || v === 'full' ? v : amical ? 'table' : 'full'; } catch { return amical ? 'table' : 'full'; }
};

/** Ce que manipule l'écran de saisie, qu'on soit connecté ou venu par un lien (D-057). */
interface Pad {
  eventId: string; label: string; opponent: string;
  back: { href: string; text: string } | null;
  players: { person_id: string; name: string; jersey: number | null }[];
  actions: Action[]; live: boolean; quarter: number; numbers: Record<string, number>;
  /** Après coup (D-103) : le score enregistré du match. Jamais de direct, jamais de score touché sans demande. */
  after?: { us: number | null; them: number | null } | null;
  /** La table de marque (D-176) : le lien de saisie, la compétition, le lieu, le genre et les couleurs de l'équipe. */
  link?: string | null; amical?: boolean; isHome?: boolean | null; gender?: TeamGender; color?: string | null; logo?: string | null;
  sheetHref?: string | null;
}
interface Backend {
  add(person: string | null, kind: ActionKind, quarter: number): Promise<Action>;
  undo(id: string): Promise<void>;
  live(action: 'start' | 'quarter' | 'finish', value?: string): Promise<void>;
  setNumbers(numbers: Record<string, number>): Promise<void>;
  onFinish(): void;
  /** Après coup : le score du match devient celui de la saisie (sur demande seulement). */
  scoreFromActions?(): Promise<{ us: number; them: number }>;
  /** Relire le journal (en revenant de la table de marque, qui l'a enrichi). */
  reload?(): Promise<{ actions: Action[]; live: boolean; quarter: number }>;
}

export async function renderLiveStats(root: HTMLElement, ctx: Ctx, eventId: string, sub?: string): Promise<void> {
  if (sub === 'feuille') { await renderClubSheetApp(root, ctx, eventId); return; }
  const ev = await eventById(eventId);
  const match = ev ? (await matchesFor([eventId]))[0] : undefined;
  if (!ev || !match) { mount(root, html`<div class="empty"><p class="empty-title">Match introuvable.</p></div>`); return; }
  const date = localDate(new Date(ev.starts_at));
  const back = `#/e/${eventId}/${date}`;
  if (!(await canTrack(eventId).catch(() => false))) { location.hash = back; return; }
  const [roster, responses, lineup, actions, avail, leads] = await Promise.all([occurrenceRoster(eventId, date), responsesFor(eventId, date),
    matchLineup(eventId).catch(() => []), matchActions(eventId).catch(() => [] as Action[]), loadAvail(), leadsFor(teamOf(ctx, ev.team_id)?.club_id ?? ctx.club?.id)]);
  // Sur le terrain : les retenues si la composition existe (le même compte que la fiche, D-103), sinon
  // les convoquées non absentes. Une joueuse placée depuis l'autre équipe du groupe est dans « Qui ? ».
  const att = resolveAttendance(roster.filter(r => !r.invited).map(r => r.person_id), roster.filter(r => r.invited).map(r => r.person_id),
    responses, attCtx(avail, ev, { date, start: new Date(ev.starts_at) }, match, leads));   // une incertaine est sur la liste (D-116)
  const absent = new Set(att.absent.map(a => a.personId));
  const retained = new Set(retainedOf(lineup, absent));
  const placed = [...retained].filter(pid => !roster.some(r => r.person_id === pid));
  const group = placed.length ? await teamRoster(ev.team_id).catch(() => []) : [];
  const onCourt = [
    ...roster.filter(r => (retained.size ? retained.has(r.person_id) : !absent.has(r.person_id))),
    ...group.filter(r => placed.includes(r.person_id)),
  ];
  const after = statsMode(new Date(ev.starts_at), match) === 'after';
  statsPad(root, {
    eventId, label: squadName(ctx, match.squad_id) ?? teamOf(ctx, ev.team_id)?.name ?? 'Nous', opponent: match.opponent,
    back: { href: back, text: '‹ Fiche du match' },
    players: onCourt.map(r => ({ person_id: r.person_id, name: displayName(r), jersey: r.jersey_number })),
    actions, live: !!match.live, quarter: match.live_quarter ?? 1, numbers: match.numbers ?? {},
    after: after ? { us: match.score_us, them: match.score_them } : null,
    amical: match.competition === 'amical', isHome: match.is_home, gender: teamOf(ctx, ev.team_id)?.gender ?? 'X',
    color: ctx.club?.primary_color ?? null, logo: ctx.club?.logo_url ?? null, sheetHref: `#/stats/${eventId}/feuille`,
  }, {
    reload: async () => {
      const [a, m] = await Promise.all([matchActions(eventId), matchesFor([eventId]).then(x => x[0])]);
      return { actions: a, live: !!m?.live, quarter: m?.live_quarter ?? 1 };
    },
    add: (person, kind, quarter) => addAction(eventId, person, kind, quarter),
    undo: id => deleteAction(id).then(() => undefined),
    live: (action, value) => matchLive(eventId, action, value ?? null).then(() => undefined),
    setNumbers: numbers => matchSetNumbers(eventId, numbers).then(() => undefined),
    onFinish: () => { location.hash = back; },
    scoreFromActions: () => matchScoreFromActions(eventId),
  });
}

/** Saisie par un lien sans compte (D-057) : seulement pendant le match. */
export async function renderStatsLink(root: HTMLElement, token: string): Promise<void> {
  let offline = false;
  const info = await statLinkInfo(token).catch(() => { offline = true; return null; });
  if (!info && offline) {
    // Hors réseau : la table de marque de ce téléphone se rouvre quand même (D-176).
    mount(root, html`<main class="screen ls-link"><div class="empty"><p class="empty-title">Pas de réseau.</p><p>Le lien de saisie se rouvrira au retour du réseau.</p></div></main>`);
    offlineResume(root, { link: token });
    return;
  }
  if (!info) {
    mount(root, html`<main class="screen ls-link"><div class="empty"><p class="empty-title">Ce lien n'est plus valable.</p>
      <p>Un lien de saisie ne vaut que pendant le match (d'une heure avant le coup d'envoi jusqu'au score final). Demande un nouveau lien au coach.</p></div>
      <p class="login-foot">${legalFootLink(dataHref())}</p></main>`);
    return;
  }
  const host = document.createElement('main');
  host.className = 'ls-link';
  // Mentions légales et données du club (D-103), sous la saisie : <lien>/donnees.
  const foot = document.createElement('p');
  foot.className = 'login-foot lg-stats-foot';
  mount(foot, legalFootLink(dataHref({ via: `/s/${token}` })));
  root.replaceChildren(host, foot);
  statsPad(host, {
    eventId: info.event_id, label: info.label, opponent: info.opponent, back: null,
    players: info.players, actions: info.actions, live: info.live, quarter: info.quarter ?? 1, numbers: info.numbers ?? {}, link: token,
  }, {
    reload: async () => { const i = await statLinkInfo(token); if (!i) throw new Error('lien expiré'); return { actions: i.actions, live: i.live, quarter: i.quarter ?? 1 }; },
    add: (person, kind, quarter) => statLinkAct(token, person, kind, quarter),
    undo: id => statLinkUndo(token, id),
    live: (action, value) => statLinkLive(token, action, value ?? null),
    setNumbers: numbers => statLinkNumbers(token, numbers),
    onFinish: () => mount(root, html`<main class="screen ls-link"><div class="empty"><p class="empty-title">Match terminé, score enregistré.</p>
      <p>Merci pour la saisie ! Ce lien ne sert plus.</p></div></main>`),
  }, `Saisie du match · lien valable jusqu'à ${new Date(info.valid_until).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h')}`);
}

function statsPad(root: HTMLElement, pad: Pad, be: Backend, note?: string): void {
  const { eventId, label, opponent } = pad;
  let actions = pad.actions, live = pad.live, quarter = pad.quarter, numbers = pad.numbers;
  // Après coup (D-103) : ni direct, ni quart-temps enregistré, ni « Terminer » ; le score du match ne bouge pas.
  let official = pad.after ?? null;
  const after = !!official;
  let picked: string | null = null;
  let mode: Mode = readMode(eventId, !!pad.amical);
  if (after && mode === 'table') mode = 'full';      // match joué : la table est fermée, la saisie se fait après coup
  let tableClose: (() => void) | null = null;
  const setMode = (m: Mode) => { mode = m; try { localStorage.setItem(modeKey(eventId), m); } catch { /* préférence non retenue */ } };
  const nameOf = new Map(pad.players.map(p => [p.person_id, p.name]));
  // Numéro de la joueuse pour CE match : réglé par la saisie, sinon celui de sa fiche.
  const numberOf = (pid: string) => numbers[pid] ?? pad.players.find(p => p.person_id === pid)?.jersey ?? null;
  const players = () => [...pad.players].sort((a, b) => (numberOf(a.person_id) ?? 999) - (numberOf(b.person_id) ?? 999));

  /** La table de marque, en plein écran ; en revenir relit le journal (elle l'a enrichi). */
  const openTableMode = () => {
    if (tableClose) return;
    tableClose = openTable({
      kind: 'match', eventId, link: pad.link ?? null, kicker: pad.amical ? 'Match amical' : undefined,
      us: { name: label, color: pad.color ?? '' }, them: { name: opponent, color: '#15803D' },
      gender: pad.gender, isHome: pad.isHome, players: pad.players, numbers, logo: pad.logo, sheetHref: pad.sheetHref ?? null,
      onMode: m => { tableClose = null; setMode(m); void refreshJournal(); },
      onClose: () => { tableClose = null; if (pad.back) location.hash = pad.back.href; else void refreshJournal(); },
    });
  };
  const refreshJournal = async () => {
    try { const x = await be.reload?.(); if (x) { actions = x.actions; live = x.live; quarter = x.quarter; } } catch { /* la saisie continue avec ce qu'on a */ }
    draw();
  };
  const draw = () => {
    const sc = score(actions);
    const check = official ? scoreCheck(official, sc) : 'none';
    const offTxt = official && official.us !== null && official.them !== null ? `${official.us} – ${official.them}` : null;
    mount(root, html`
      <section class="screen ls ${after ? 'ls-after' : ''}">
        <div class="pp-nav">${pad.back ? html`<a class="back" href="${pad.back.href}">${pad.back.text}</a>` : html`<span class="muted small">${note ?? ''}</span>`}
          <span class="chip ${live ? 'chip-ko' : 'chip-mute'}">${live ? '● En direct' : after ? 'Saisie après coup' : 'Pas commencé'}</span></div>
        <div class="ls-score" role="status" aria-live="polite">
          <span class="ls-team">${label}</span><b>${sc.us}</b><span class="ls-sep">–</span><b>${sc.them}</b><span class="ls-team">${opponent}</span>
        </div>
        ${after ? html`<div class="ls-afternote ${check === 'same' ? 'ok' : ''}">
          <strong>${offTxt ? `Score du match : ${offTxt}` : 'Pas encore de score pour ce match'}</strong>
          <span>${check === 'same' ? '✓ La saisie correspond au score du match.'
            : 'Match terminé : la saisie ne passe rien en direct et ne change pas le score.'}</span></div>` : ''}
        <div class="seg ls-q" role="radiogroup" aria-label="Quart-temps">${[1, 2, 3, 4, 5].map(q => html`
          <button type="button" role="radio" data-q="${q}" aria-pressed="${String(quarter === q)}">${QLABEL(q)}</button>`)}</div>
        ${live || after ? '' : html`<button type="button" class="btn btn-primary ls-start" id="ls-start">▶ Lancer le direct</button>`}
        <div class="seg ls-mode" role="radiogroup" aria-label="Saisie" data-guide="ls-mode">
          <button type="button" role="radio" data-mode="full" aria-pressed="${String(mode === 'full')}">Stats</button>
          <button type="button" role="radio" data-mode="score" aria-pressed="${String(mode === 'score')}">Score seul</button>
          ${after ? '' : html`<button type="button" role="radio" data-mode="table" aria-pressed="${String(mode === 'table')}">Table de marque</button>`}</div>
        ${mode === 'table' ? html`
          <div class="empty ls-table-note"><p class="empty-title">La table de marque</p>
            <p>Un seul téléphone tient la table du match : le chrono, les fautes et les temps-morts y vivent, même hors réseau. Hors compétition FFBB.</p>
            <button type="button" class="btn btn-primary" id="ls-table">Ouvrir la table de marque</button></div>` : ''}
        ${mode === 'table' ? '' : mode === 'score' ? html`
          <p class="eyebrow">${label}</p>
          <div class="ls-pad ls-big">${US.map(([k, l]) => html`<button type="button" class="ls-act pts" data-k="${k}">${l}</button>`)}</div>
          <p class="eyebrow">Adversaire · ${opponent}</p>
          <div class="ls-pad ls-big ls-opp">${OPP.filter(([k]) => k !== 'opp_foul').map(([k, l]) => html`<button type="button" class="ls-act opp" data-k="${k}">${l}</button>`)}</div>`
        : html`
          <div class="ls-plhead"><span class="eyebrow">Qui ?</span><button type="button" class="btn btn-link btn-sm" id="ls-nums">Numéros du match</button></div>
          <div class="ls-players" role="radiogroup" aria-label="Qui a fait l’action">${players().map(p => html`
            <button type="button" role="radio" class="ls-pl" data-pl="${p.person_id}" aria-pressed="${String(picked === p.person_id)}">
              <b>${numberOf(p.person_id) ?? ''}</b><span>${p.name}</span></button>`)}</div>
          <div class="ls-pad">${PAD.map(([k, l]) => html`<button type="button" class="ls-act ${k.endsWith('_miss') ? 'miss' : k === 'p2' || k === 'p3' || k === 'ft' ? 'pts' : ''}" data-k="${k}">${l}</button>`)}</div>
          <p class="eyebrow">Adversaire · ${opponent}</p>
          <div class="ls-pad ls-opp">${OPP.map(([k, l]) => html`<button type="button" class="ls-act opp" data-k="${k}">${l}</button>`)}</div>`}
        <div class="btn-row">
          <button type="button" class="btn btn-ghost btn-sm" id="ls-undo" ${actions.length ? '' : 'disabled'}>↶ Annuler la dernière action</button>
          ${after ? html`<button type="button" class="btn btn-primary btn-sm" id="ls-done">C’est saisi</button>`
            : html`<button type="button" class="btn btn-danger-ghost btn-sm" id="ls-end">Terminer le match</button>`}
        </div>
        ${after && check === 'diff' && be.scoreFromActions ? html`<button type="button" class="btn btn-link btn-sm ls-rescore" id="ls-rescore">${offTxt ? 'Remplacer le score du match par celui de la saisie' : 'Enregistrer le score d’après la saisie'}</button>` : ''}
        <ol class="ls-feed">${[...actions].reverse().slice(0, 8).map(a => html`<li><span class="muted small">${QLABEL(a.quarter)}</span>
          <strong>${a.person_id ? nameOf.get(a.person_id) ?? '—' : a.kind.startsWith('us') ? label : a.kind.startsWith('opp') ? opponent : 'Table'}</strong><span>${ACTION_LABEL[a.kind]}</span></li>`)}</ol>
      </section>`);

    $(root, '#ls-start')?.addEventListener('click', async () => {
      try { await be.live('start'); live = true; toast('Direct lancé : le score s’affiche sur la page publique'); draw(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-q]')) b.addEventListener('click', async () => {
      const q = Number(b.dataset.q);
      if (after) { quarter = q; draw(); return; }   // après coup : le quart-temps de la saisie, rien d'enregistré sur le match
      try { await be.live('quarter', String(q)); quarter = q; draw(); } catch { toast('Impossible.', 'ko'); }
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-pl]')) b.addEventListener('click', () => {
      picked = b.dataset.pl!;   // reste choisie pour enchaîner les actions
      $$(root, '[data-pl]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.pl === picked)));
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-k]')) b.addEventListener('click', async () => {
      const k = b.dataset.k as ActionKind;
      const team = k.startsWith('opp') || k.startsWith('us');   // sans joueuse : l'adversaire, ou nous en « score seul »
      if (!team && !picked) { toast('Choisis d’abord qui a fait l’action.', 'ko'); return; }
      b.disabled = true;
      try {
        const a = await be.add(team ? null : picked, k, quarter);
        actions = [...actions, a];
        try { navigator.vibrate?.(15); } catch { /* sans vibreur */ }
        if (!live && !after) { await be.live('start').catch(() => undefined); live = true; }
        draw();
      } catch { b.disabled = false; toast('Action non enregistrée. Réessaie.', 'ko'); }
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-mode]')) b.addEventListener('click', () => {
      const m = b.dataset.mode;
      setMode(m === 'score' ? 'score' : m === 'table' ? 'table' : 'full');
      draw();
      if (mode === 'table') openTableMode();
    });
    $(root, '#ls-table')?.addEventListener('click', () => openTableMode());
    $(root, '#ls-nums')?.addEventListener('click', () => openNumbers());
    $(root, '#ls-undo')?.addEventListener('click', async () => {
      const last = actions[actions.length - 1];
      if (!last) return;
      try { await be.undo(last.id); actions = actions.slice(0, -1); toast(`Annulé : ${ACTION_LABEL[last.kind]}`); draw(); }
      catch { toast('Impossible.', 'ko'); }
    });
    const end = $<HTMLButtonElement>(root, '#ls-end');
    if (end) confirmTwice(end, `Confirmer : score final ${sc.us} – ${sc.them}`, async () => {
      try { await be.live('finish'); toast('Match terminé, score enregistré'); be.onFinish(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
    $(root, '#ls-done')?.addEventListener('click', () => { toast('Stats enregistrées'); be.onFinish(); });
    $(root, '#ls-rescore')?.addEventListener('click', () => openRescore(sc, offTxt));
  };
  /** Après coup, sur demande : le score du match devient celui de la saisie, une fois toutes les actions marquées saisies. */
  const openRescore = (sc: { us: number; them: number }, offTxt: string | null) => openSheet(offTxt ? 'Remplacer le score du match' : 'Score d’après la saisie', html`
    <div class="ls-cmp">
      ${offTxt ? html`<div><small>Score du match</small><b>${offTxt}</b></div>` : ''}
      <div><small>Score de la saisie</small><b>${sc.us} – ${sc.them}</b></div>
    </div>
    <label class="check"><input type="checkbox" id="ls-all"><span><strong>Tous les paniers des deux équipes sont saisis</strong><br>
      <span class="muted">Sinon, le score du match serait faux : garde ${offTxt ? 'le score enregistré' : 'la fiche sans score'} et saisis ce qui manque.</span></span></label>
    <button type="button" class="btn btn-primary" id="ls-apply" disabled>${offTxt ? `Remplacer par ${sc.us} – ${sc.them}` : `Enregistrer ${sc.us} – ${sc.them}`}</button>
    <p class="hint">Le match reste terminé : rien ne passe en direct.</p>`, (sheet, close) => {
    const all = $<HTMLInputElement>(sheet, '#ls-all')!, go = $<HTMLButtonElement>(sheet, '#ls-apply')!;
    all.addEventListener('change', () => { go.disabled = !all.checked; });
    go.addEventListener('click', async () => {
      if (!all.checked || !be.scoreFromActions) return;
      go.disabled = true;
      try { const r = await be.scoreFromActions(); official = { us: r.us, them: r.them }; close(); toast(`Score du match : ${r.us} – ${r.them}`); draw(); }
      catch { go.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  });
  /** Les numéros pour ce match (maillots prêtés, numéros changés) : par défaut ceux des fiches. */
  const openNumbers = () => openSheet('Numéros du match', html`
    <form class="form" id="f-nums" novalidate>
      <ul class="ls-nums">${players().map(p => html`<li><label><span>${p.name}</span>
        <input type="number" inputmode="numeric" min="0" max="99" name="${p.person_id}" value="${numberOf(p.person_id) ?? ''}" aria-label="Numéro de ${p.name}"></label></li>`)}</ul>
      <p class="form-msg" role="alert"></p>
      <p class="hint">Pour ce match seulement : les fiches de l’effectif ne changent pas.</p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-nums')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const out: Record<string, number> = {};
      for (const [k, v] of new FormData(f)) { const t = String(v).trim(); if (t === '') continue; const n = Number(t); if (!Number.isInteger(n) || n < 0 || n > 99) { $(f, '.form-msg')!.textContent = 'Un numéro de 0 à 99.'; return; } out[k] = n; }
      const seen = Object.values(out), dup = seen.find((n, i) => seen.indexOf(n) !== i);
      if (dup !== undefined) { $(f, '.form-msg')!.textContent = `Le ${dup} est pris deux fois.`; return; }
      try { await be.setNumbers(out); numbers = out; close(); toast('Numéros du match enregistrés'); draw(); }
      catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible.'; }
    });
  });
  draw();
  if (mode === 'table') openTableMode();
}

/** Fiche du match (D-058) : en direct, qui saisit, les fautes à surveiller et les meilleures ;
 *  après, les meilleures et la feuille complète. */
export async function renderStatsBlock(el: HTMLElement, ctx: Ctx, eventId: string, match: MatchRow, staff: boolean,
  nameOf: Map<string, string>, view: 'live' | 'after' = 'after', start: Date | null = null): Promise<void> {
  // Match joué (D-103) : la saisie se fait « après coup », sans direct.
  const over = !!start && statsMode(start, match) === 'after';
  const [actions, tracker, accounts, link] = await Promise.all([
    matchActions(eventId).catch(() => [] as Action[]), canTrack(eventId).catch(() => false),
    staff ? matchAccounts(eventId).catch(() => []) : Promise.resolve([]),
    staff ? activeStatLink(eventId).catch(() => null) : Promise.resolve(null),
  ]);
  const lines = boxScore(actions);
  const name = (pid: string) => nameOf.get(pid) ?? '—';
  const num = (pid: string) => match.numbers?.[pid] ?? null;
  const LEAD: ['pts' | 'reb' | 'ast', string, string][] = [['pts', 'pts', 'POINTS'], ['reb', 'reb', 'REBONDS'], ['ast', 'pd', 'PASSES DÉC.']];
  const leaders = LEAD.map(([k, unit, label]) => {
    const l = [...lines].sort((a, b) => b[k] - a[k])[0];
    return l && l[k] > 0 ? { l, k, unit, label } : null;
  }).filter((x): x is NonNullable<typeof x> => x !== null);
  const pct = (m: number, a: number) => (a ? `${m}/${a}` : '–');

  if (view === 'live') {
    const fouls = lines.filter(l => l.fouls >= 3).sort((a, b) => b.fouls - a.fouls);
    const who = match.statman_user ? accounts.find(a => a.user_id === match.statman_user)?.name ?? 'Une personne désignée' : null;
    mount(el, html`
      ${staff || tracker ? html`
        <section class="block ls-who">
          <span class="ls-dot ${match.live ? 'on' : ''}" aria-hidden="true"></span>
          <span class="ls-who-txt"><strong>${link && !over ? 'Saisie par un lien sans compte' : who ? `${who} saisit les stats` : over ? (actions.length ? 'Stats saisies' : 'Pas encore de stats') : 'Personne ne saisit encore'}</strong>
            <span class="muted small">${match.live ? 'Direct en cours · le score passe sur la page publique' : over ? 'Match terminé : les stats se saisissent après coup, sans direct' : 'Le direct démarre à la première action saisie'}</span></span>
          ${tracker ? html`<a class="btn btn-primary btn-sm" href="#/stats/${eventId}">${over ? 'Saisir après coup' : 'Saisir'}</a>` : ''}
        </section>
        ${staff ? html`<section class="block ls-give">
          ${over ? '' : html`<button type="button" class="btn btn-ghost btn-sm" id="ls-link">${link ? 'Renvoyer le lien de saisie' : 'Lien de saisie sans compte'}</button>`}
          ${accounts.length ? html`<label class="field"><span>Ou confier la saisie à (compte du club)</span>
            <select id="ls-statman"><option value="">Personne</option>${accounts.map(a => html`<option value="${a.user_id}" ${match.statman_user === a.user_id ? 'selected' : ''}>${a.name}</option>`)}</select></label>` : ''}
        </section>
        ${over ? '' : html`<div id="ls-tadmin"></div>`}` : ''}` : ''}
      ${fouls.length ? html`<section class="block"><h3>Fautes à surveiller</h3><div class="ls-fouls">${fouls.map(l => html`
        <span class="chip ${l.fouls >= 4 ? 'chip-ko' : 'chip-warn'}">${num(l.person_id) !== null ? `${num(l.person_id)} ` : ''}${name(l.person_id)} · ${l.fouls} fautes</span>`)}</div></section>` : ''}
      ${leaders.length ? html`<section class="block"><h3>En tête du match</h3><ul class="ls-lead">${leaders.map(x => html`<li>
        <span class="ls-num">${num(x.l.person_id) ?? name(x.l.person_id).charAt(0)}</span>
        <span class="ls-lead-txt"><strong>${name(x.l.person_id)}</strong><span class="muted small">${x.k === 'pts' ? `${x.l.p2[0] + x.l.p3[0]}/${x.l.p2[1] + x.l.p3[1]} aux tirs · ${x.l.p3[0]} à 3 pts` : x.k === 'reb' ? `${x.l.oreb} offensifs` : `${x.l.stl} interceptions`}</span></span>
        <b>${x.l[x.k]} ${x.unit}</b></li>`)}</ul></section>`
        : html`<p class="muted ls-empty">${actions.length ? (over ? 'Score saisi, sans stats individuelles.' : 'Score en direct, sans stats individuelles.') : match.live ? 'Le score est en direct ; aucune action pour l’instant.' : over ? 'Match terminé : rien de saisi.' : 'Le direct commence au coup d’envoi.'}</p>`}`);
    $(el, '#ls-link')?.addEventListener('click', () => void openStatLink(eventId));
    // La table de marque (D-176) : qui la tient, la demande de main à accepter, la libérer, le lien de suivi des parents.
    const ta = $(el, '#ls-tadmin');
    if (ta) void renderTableAdmin(ta, eventId).catch(() => undefined);
    $<HTMLSelectElement>(el, '#ls-statman')?.addEventListener('change', async (e) => {
      try { await matchLive(eventId, 'statman', (e.target as HTMLSelectElement).value || null); toast('Saisie confiée'); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
    // En direct : la fiche se met à jour toute seule tant qu'elle est ouverte.
    if (match.live && el.isConnected) setTimeout(() => { if (el.isConnected) void renderStatsBlock(el, ctx, eventId, match, staff, nameOf, view, start); }, 15000);
    return;
  }

  if (!actions.length) { mount(el, tracker ? html`<section class="block"><h3>Stats du match</h3><p class="muted small">Rien de saisi.</p>
    <a class="btn btn-ghost btn-sm" href="#/stats/${eventId}">Saisir après coup</a></section>` : html``); return; }
  const qs = byQuarter(actions);
  mount(el, html`
    <section class="block">
      <h3>${leaders.length ? 'Le top du match' : 'Le score'}</h3>
      ${leaders.length ? html`<div class="ls-tops">${leaders.map(x => html`<div><b>${x.l[x.k]}</b><small>${x.label}</small><strong>${name(x.l.person_id)}</strong></div>`)}</div>` : ''}
      ${qs.length ? html`<p class="muted small">${qs.map(q => `${QLABEL(q.q)} ${q.us}-${q.them}`).join(' · ')}</p>` : ''}
      ${lines.length ? html`<details class="ls-full"><summary>Feuille complète</summary><div class="ls-table-wrap"><table class="ls-table">
        <thead><tr><th scope="col">Nom</th><th scope="col">Pts</th><th scope="col">2 pts</th><th scope="col">3 pts</th><th scope="col">LF</th><th scope="col">Reb</th><th scope="col">PD</th><th scope="col">Int</th><th scope="col">BP</th><th scope="col">F</th><th scope="col">Éval</th></tr></thead>
        <tbody>${lines.map(l => html`<tr><th scope="row">${name(l.person_id)}</th><td><b>${l.pts}</b></td><td>${pct(...l.p2)}</td><td>${pct(...l.p3)}</td><td>${pct(...l.ft)}</td>
          <td>${l.reb}</td><td>${l.ast}</td><td>${l.stl}</td><td>${l.tov}</td><td>${l.fouls}</td><td>${l.eval}</td></tr>`)}</tbody>
      </table></div></details>` : ''}
      ${tracker ? html`<a class="btn btn-link btn-sm" href="#/stats/${eventId}">Corriger la saisie</a>` : ''}
      ${staff ? html`<a class="btn btn-ghost btn-sm ls-sheet" href="#/stats/${eventId}/feuille" data-guide="ls-sheet">Feuille de match du club</a>` : ''}
    </section>`);
}

/** Un lien à envoyer (SMS, WhatsApp…) pour saisir sans compte ; il ne vaut que pendant le match. */
export async function openStatLink(eventId: string): Promise<void> {
  let token: string;
  try { token = await statLinkCreate(eventId); } catch { toast('Lien impossible pour le moment.', 'ko'); return; }
  const url = `${location.origin}/s/${token}`;
  openSheet('Lien de saisie', html`
    <p>Envoie ce lien à la personne qui tient les stats (un parent à la table…). <strong>Pas besoin de compte.</strong></p>
    <label class="field"><span>Lien</span><input id="sl-url" readonly value="${url}"></label>
    <div class="btn-row">
      <button type="button" class="btn btn-primary btn-sm" id="sl-copy">Copier</button>
      ${'share' in navigator ? html`<button type="button" class="btn btn-ghost btn-sm" id="sl-share">Partager</button>` : ''}
    </div>
    <p class="hint">Valable d'une heure avant le coup d'envoi à quatre heures après, et plus du tout une fois le score final enregistré. Un nouveau lien remplace celui-ci.</p>
    <button type="button" class="btn btn-danger-ghost" id="sl-cut">Couper le lien</button>`, (sheet, close) => {
    const input = $<HTMLInputElement>(sheet, '#sl-url')!;
    $(sheet, '#sl-copy')!.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(url); toast('Lien copié'); } catch { input.select(); toast('Sélectionné : copie-le'); }
    });
    $(sheet, '#sl-share')?.addEventListener('click', () => { void navigator.share({ title: 'Saisie du match', text: 'Le lien pour saisir les stats du match :', url }).catch(() => undefined); });
    $(sheet, '#sl-cut')!.addEventListener('click', async () => {
      try { await statLinkRevoke(eventId); close(); toast('Lien coupé'); } catch { toast('Impossible.', 'ko'); }
    });
  });
}
